// tabs/OverviewTab.jsx
// DUAL-SOURCE Overview tab: Your Filtered Market + Similar Games Network
// Shows both Game Discovery filtered games analysis and Gamalytics wishlist network

import React from 'react';
import { CheckCircle, AlertTriangle, TrendingUp, Target, Filter, Network, Info, AlertCircle } from 'lucide-react';
import HelpIcon from '../shared/HelpIcon';
import SwotQuadrant from '../shared/SwotQuadrant';
import MetricCard from '../shared/MetricCard';
import { POSITION_EXPLANATIONS, TREND_EXPLANATIONS, STRENGTH_EXPLANATIONS } from '../shared/constants';

/**
 * Format currency for display
 */
const formatCurrency = (value) => {
    if (!value || value === 0) return '$0';
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
    return `$${value.toFixed(0)}`;
};

/**
 * Comparison Bar Component - Shows metric vs average
 */
const ComparisonBar = ({ label, percentage, value, average }) => {
    const isAboveAverage = percentage >= 100;
    const barColor = isAboveAverage ? 'bg-green-500' : 'bg-amber-500';
    const textColor = isAboveAverage ? 'text-green-700' : 'text-amber-700';
    const displayPercent = Math.min(200, Math.max(0, percentage)); // Cap at 200% for display

    return (
        <div className="space-y-1">
            <div className="flex justify-between text-sm">
                <span className="text-gray-700">{label}</span>
                <span className={`font-medium ${textColor}`}>
                    {percentage.toFixed(0)}% of avg
                </span>
            </div>
            <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                    className={`h-full ${barColor} transition-all`}
                    style={{ width: `${displayPercent / 2}%` }} // Divide by 2 so 100% = 50% bar
                />
                {/* Average marker at 50% */}
                <div className="absolute top-0 left-1/2 h-full w-0.5 bg-gray-600" />
            </div>
            <div className="flex justify-between text-xs text-gray-500">
                <span>{formatCurrency(value)}</span>
                <span>Avg: {formatCurrency(average)}</span>
            </div>
        </div>
    );
};

/**
 * Overview tab component - DUAL SOURCE ANALYSIS
 * Left: Your Filtered Market (from Game Discovery)
 * Right: Similar Games Network (from Gamalytics)
 */
const OverviewTab = ({ analysis }) => {
    // Extract both data sources
    const filteredMarket = analysis?.filteredMarket || { available: false };
    const gamalyticsNetwork = analysis?.gamalyticsNetwork || {};
    
    // Extract Gamalytics data (for backwards compatibility)
    const summary = gamalyticsNetwork?.summary || {};
    const overview = summary?.overview || {};
    const marketPosition = overview?.marketPosition || { position: 'unknown', trend: 'stable', strength: 'medium' };
    const keyMetrics = summary?.keyMetrics || {};
    const strengths = summary?.strengths || [];
    const weaknesses = summary?.weaknesses || [];
    const opportunities = summary?.opportunities || [];
    const threats = summary?.threats || [];

    return (
        <div className="p-6 space-y-8">
            {/* Header explaining both analyses */}
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                <div className="flex items-start gap-2">
                    <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                        <h3 className="font-medium text-blue-900 mb-2">About These Analyses</h3>
                        <div className="text-sm text-blue-800 space-y-1">
                            <p><strong>Your Filtered Market:</strong> Shows your position among the {filteredMarket.scope?.totalGames || 0} games you filtered in Game Discovery.</p>
                            <p><strong>Similar Games Network:</strong> Shows related games based on user wishlist overlap (Gamalytics API data).</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* TWO-COLUMN LAYOUT */}
            <div className="grid grid-cols-2 gap-6">
                {/* LEFT COLUMN: Your Filtered Market */}
                <div className="space-y-6">
                    <div className="bg-white border-2 border-purple-200 rounded-lg p-6">
                        <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
                            <Filter className="w-5 h-5 text-purple-600" />
                            Your Filtered Market
                            <HelpIcon
                                title="Your Filtered Market"
                                description="Analysis of the games you filtered in Game Discovery. Shows your position within your target market segment."
                            />
                        </h3>

                        {filteredMarket.available ? (
                            <>
                                {/* Market Share within filtered games */}
                                <div className="mb-6">
                                    <MetricCard
                                        label="Market Share (Filtered Games)"
                                        value={`${filteredMarket.marketShare.percentage.toFixed(1)}%`}
                                        tooltip={filteredMarket.marketShare.explanation}
                                    />
                                    <div className="text-center mt-2 text-sm text-gray-600">
                                        Rank: #{filteredMarket.marketShare.rank} of {filteredMarket.marketShare.totalRank} games
                                    </div>
                                </div>

                                {/* Comparison to averages */}
                                <div className="space-y-4">
                                    <div className="text-sm font-medium text-gray-700 mb-2">Performance vs Filtered Average</div>
                                    <ComparisonBar
                                        label="Revenue"
                                        percentage={filteredMarket.comparison.revenue.percentOfAverage}
                                        value={filteredMarket.comparison.revenue.value}
                                        average={filteredMarket.comparison.revenue.average}
                                    />
                                    <ComparisonBar
                                        label="Wishlists"
                                        percentage={filteredMarket.comparison.wishlists.percentOfAverage}
                                        value={filteredMarket.comparison.wishlists.value}
                                        average={filteredMarket.comparison.wishlists.average}
                                    />
                                    <ComparisonBar
                                        label="Followers"
                                        percentage={filteredMarket.comparison.followers.percentOfAverage}
                                        value={filteredMarket.comparison.followers.value}
                                        average={filteredMarket.comparison.followers.average}
                                    />
                                </div>

                                <div className="mt-4 p-3 bg-purple-50 rounded text-xs text-purple-800">
                                    <strong>Scope:</strong> {filteredMarket.scope.description}
                                </div>
                            </>
                        ) : (
                            <div className="text-center py-8 text-gray-500">
                                <AlertCircle className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                                <p className="font-medium mb-1">Not Available</p>
                                <p className="text-sm">{filteredMarket.reason}</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* RIGHT COLUMN: Similar Games Network (Gamalytics) */}
                <div className="space-y-6">
                    <div className="bg-white border-2 border-blue-200 rounded-lg p-6">
                        <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
                            <Network className="w-5 h-5 text-blue-600" />
                            Similar Games Network
                            <HelpIcon
                                title="Similar Games Network"
                                description="Games that users also wishlisted, discovered through Gamalytics API. Shows broader market context."
                            />
                        </h3>

                        {/* Market Position from Gamalytics */}
                        <div className="mb-6">
                            <div className="text-sm font-medium text-gray-700 mb-3">Market Position</div>
                            <div className="grid grid-cols-3 gap-2">
                                <div className="p-3 bg-gray-50 rounded text-center">
                                    <div className="text-xs text-gray-600">Position</div>
                                    <div className="text-sm font-bold capitalize">{marketPosition.position}</div>
                                </div>
                                <div className="p-3 bg-gray-50 rounded text-center">
                                    <div className="text-xs text-gray-600">Trend</div>
                                    <div className="text-sm font-bold capitalize">{marketPosition.trend}</div>
                                </div>
                                <div className="p-3 bg-gray-50 rounded text-center">
                                    <div className="text-xs text-gray-600">Strength</div>
                                    <div className="text-sm font-bold capitalize">{marketPosition.strength}</div>
                                </div>
                            </div>
                        </div>

                        {/* Gamalytics metrics */}
                        <div className="space-y-4 mb-6">
                            <MetricCard
                                label="Market Share (Network)"
                                value={`${(keyMetrics.marketShare || 0).toFixed(1)}%`}
                                tooltip={keyMetrics.marketShareExplanation || 'Share of revenue among similar games'}
                            />
                            <MetricCard
                                label="Audience Engagement"
                                value={`${(keyMetrics.audienceEngagement || 0).toFixed(1)}%`}
                                tooltip={keyMetrics.engagementExplanation || 'Engagement based on wishlist age distribution'}
                            />
                            <MetricCard
                                label="Geographic Reach"
                                value={`${(keyMetrics.geographicReach || 0).toFixed(1)}%`}
                                tooltip={keyMetrics.reachExplanation || 'Market presence across countries'}
                            />
                        </div>

                        <div className="p-3 bg-blue-50 rounded text-xs text-blue-800">
                            <strong>Scope:</strong> {summary.scope?.description || 'Analysis based on Gamalytics wishlist data'}
                        </div>
                    </div>
                </div>
            </div>

            {/* SWOT Analysis section below (combines both sources) */}
            <div className="border-t pt-6">
                <h3 className="text-lg font-medium mb-4 flex items-center">
                    Combined Market SWOT Analysis
                    <HelpIcon
                        title="Combined SWOT Analysis"
                        description="Strategic analysis combining insights from both your filtered market and the similar games network."
                    />
                </h3>
                <div className="grid grid-cols-2 gap-4">
                    <SwotQuadrant
                        title="Strengths"
                        items={strengths}
                        icon={<CheckCircle className="w-5 h-5 text-green-600" />}
                        color="bg-green-50"
                        helpText="Competitor strengths represent areas where they excel. Look for ways to differentiate or find underserved niches."
                    />
                    <SwotQuadrant
                        title="Weaknesses"
                        items={weaknesses}
                        icon={<AlertTriangle className="w-5 h-5 text-red-600" />}
                        color="bg-red-50"
                        helpText="Competitor weaknesses represent potential opportunities for you to provide better solutions or experiences."
                    />
                    <SwotQuadrant
                        title="Opportunities"
                        items={opportunities}
                        icon={<TrendingUp className="w-5 h-5 text-blue-600" />}
                        color="bg-blue-50"
                        helpText="Market opportunities show areas for growth, expansion, or innovation that competitors haven't fully addressed."
                    />
                    <SwotQuadrant
                        title="Threats"
                        items={threats}
                        icon={<Target className="w-5 h-5 text-amber-600" />}
                        color="bg-amber-50"
                        helpText="Market threats are challenges that could impact your success. Plan strategies to mitigate these risks."
                    />
                </div>
            </div>
        </div>
    );
};

export default OverviewTab;
